import Swal from 'sweetalert2';

/**
 * notify — the app's single entry point for dialogs and alerts.
 *
 * All dialogs share one look (see assets/scss/custom/plugins/_sweatalert2.scss):
 * a small tinted icon beside the title, left-aligned body text and
 * right-aligned Bootstrap buttons.
 *
 *   notify.success('Saved', 'The member was updated.');
 *   notify.error('Error', err.message);
 *   notify.toast('Copied to clipboard');
 *
 *   if (await notify.confirm({ title: 'Reverse transaction', text: '…', confirmText: 'Reverse', danger: true })) …
 *   if (await notify.confirmDelete({ name: member.fullName })) …
 *   const reason = await notify.promptReason({ title: 'Reject claim' }); if (reason) …
 */

const BASE_CLASSES = {
  popup: 'sun-swal',
  confirmButton: 'btn btn-primary',
  cancelButton: 'btn btn-light',
  denyButton: 'btn btn-danger',
  input: 'form-control',
  validationMessage: 'sun-swal__validation',
};

const base = Swal.mixin({
  buttonsStyling: false,
  reverseButtons: true,
  focusConfirm: false,
  customClass: BASE_CLASSES,
});

// SweetAlert2 replaces (not merges) `customClass`, so always spread the base set.
const classes = (overrides = {}) => ({ ...BASE_CLASSES, ...overrides });

const alert = (icon, title, text, options = {}) =>
  base.fire({ icon, title, text, confirmButtonText: 'OK', ...options });

const notify = {
  /** Escape hatch: the preconfigured SweetAlert2 instance. */
  swal: base,

  success: (title, text, options) => alert('success', title, text, options),
  error: (title, text, options) => alert('error', title, text, options),
  warning: (title, text, options) => alert('warning', title, text, options),
  info: (title, text, options) => alert('info', title, text, options),

  /** Small non-blocking toast in the top-right corner. */
  toast: (title, icon = 'success', options = {}) =>
    base.fire({
      toast: true,
      position: 'top-end',
      icon,
      title,
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      ...options,
    }),

  /**
   * Yes/no question. Resolves to true when confirmed.
   * `danger` turns the confirm button red for destructive actions.
   */
  confirm: async ({
    title = 'Are you sure?',
    text,
    html,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    danger = false,
    icon = 'warning',
    ...options
  } = {}) => {
    const result = await base.fire({
      icon,
      title,
      text,
      html,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      customClass: classes({
        confirmButton: danger ? 'btn btn-danger' : 'btn btn-primary',
      }),
      ...options,
    });
    return result.isConfirmed;
  },

  /**
   * Destructive confirmation that requires typing a keyword ("delete").
   * Resolves to true only when the keyword was typed and Delete pressed.
   */
  confirmDelete: async ({
    title = 'Delete record',
    name,
    text,
    keyword = 'delete',
    confirmText = 'Delete',
    requireTyping = true,
    ...options
  } = {}) => {
    const escape = (s) =>
      String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const body =
      text ||
      `Are you sure you want to delete${name ? ` <strong>${escape(name)}</strong>` : ' this record'}? This action cannot be undone.`;
    const result = await base.fire({
      icon: 'error',
      title,
      html: requireTyping
        ? `<p class="mb-3">${body}</p><label class="form-label fw-medium mb-1" for="swal2-input">Type "${keyword}" to confirm deletion:</label>`
        : body,
      input: requireTyping ? 'text' : undefined,
      inputPlaceholder: requireTyping ? `Type ${keyword} here` : undefined,
      inputAttributes: { autocapitalize: 'off', autocorrect: 'off', autocomplete: 'off' },
      showCancelButton: true,
      confirmButtonText: `<i class="mdi mdi-trash-can-outline me-1"></i>${confirmText}`,
      cancelButtonText: 'Cancel',
      customClass: classes({ confirmButton: 'btn btn-danger' }),
      didOpen: (popup) => {
        if (!requireTyping) return;
        const btn = base.getConfirmButton();
        const input = base.getInput();
        btn.disabled = true;
        input.addEventListener('input', () => {
          btn.disabled = input.value.trim().toLowerCase() !== keyword.toLowerCase();
        });
        void popup;
      },
      preConfirm: (value) => {
        if (requireTyping && String(value || '').trim().toLowerCase() !== keyword.toLowerCase()) {
          base.showValidationMessage(`Please type "${keyword}" to confirm.`);
          return false;
        }
        return true;
      },
      ...options,
    });
    return result.isConfirmed;
  },

  /**
   * Ask for a free-text reason. Resolves to the trimmed text, or null on cancel.
   */
  promptReason: async ({
    title = 'Provide a reason',
    text = 'Please provide a reason.',
    placeholder = 'Reason…',
    minLength = 10,
    confirmText = 'Submit',
    danger = false,
    ...options
  } = {}) => {
    const result = await base.fire({
      title,
      text,
      input: 'textarea',
      inputPlaceholder: minLength ? `${placeholder} (minimum ${minLength} characters)` : placeholder,
      inputAttributes: { rows: 4 },
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: 'Cancel',
      customClass: classes({
        confirmButton: danger ? 'btn btn-danger' : 'btn btn-primary',
      }),
      inputValidator: (value) => {
        const v = String(value || '').trim();
        if (!v) return 'A reason is required.';
        if (minLength && v.length < minLength) return `Please enter at least ${minLength} characters.`;
        return undefined;
      },
      ...options,
    });
    return result.isConfirmed ? String(result.value).trim() : null;
  },
};

export default notify;

// Dev convenience: try dialogs from the browser console, e.g.
//   notify.confirmDelete({ name: 'User Test' })
//   notify.promptReason({ title: 'Reject Client', confirmText: 'Reject Client', danger: true })
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  window.notify = notify;
}
